import type { KeyValueStore } from '@croissants/ui';
import { readString, writeString } from './storage';

/**
 * Where the guest's language is kept on this device (PRD GST-006, NFR-006).
 *
 * "The storefront opens in the browser language when it is a supported locale,
 * otherwise in English - the platform default." Once the guest touches the
 * language switch, that choice wins over the browser for every later visit, and
 * it is the choice that gets persisted onto the QR session so notifications
 * arrive in the same language (NTF-004). The resolution itself is the shared
 * provider's (`state/locale.tsx`); what is here is the KEY and the store.
 */

export const LOCALE_STORAGE_KEY = 'cib.locale';

export function readPersistedLocale(): string | null {
  return readString(LOCALE_STORAGE_KEY);
}

/**
 * The store the locale provider reads and writes through.
 *
 * The storefront's own wrapper rather than the shared `localStorage` one: it
 * keeps an in-memory copy, so a guest who scanned the code inside a camera app's
 * webview - in private mode, or with storage blocked outright - still has their
 * language remembered for the rest of the visit rather than losing it on the
 * next screen.
 *
 * It is an ordinary `KeyValueStore` and answers for the key it is HANDED, like
 * `browserStore` next to it. It used to ignore that argument and pin both halves
 * to `LOCALE_STORAGE_KEY`, which made the type a lie for a guarantee the
 * constant already carries: `state/locale.tsx` configures the shared context
 * with this same constant, and `state/session.tsx` reads it back through
 * `readPersistedLocale()` when it asks whether this guest has a preference of
 * their own at all. One constant, named at both ends - not a store that
 * overrides its caller.
 */
export const localeStore: KeyValueStore = {
  read: readString,
  write: writeString,
};
